// ─── Shared styles — earthy orange palette (matches series) ──────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'1rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.92rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.95rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.95rem' },
  th:           { background:'#a04010', color:'#fff', padding:'11px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.93rem' },
  tdE:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.92rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'1rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.88rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── Arrow helpers ────────────────────────────────────────────────────────────
function arrowDef(id, color='#c45a10') {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
      <polygon points="0 0,7 4,0 8" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Galling mechanism: micro-scale surface contact and adhesion
// ─────────────────────────────────────────────────────────────────────────────
function DiagramMechanism() {
  const W=980, H=420;

  // Draw a jagged surface profile
  function surfaceProfile(x0, y0, w, roughness, n, fill, stroke) {
    const pts = [];
    for(let i=0; i<=n; i++) {
      const x = x0 + (i/n)*w;
      const yOff = (i===0||i===n) ? 0 : roughness*(Math.sin(i*2.7)*0.5 + Math.sin(i*5.3)*0.3 + Math.sin(i*1.9)*0.2);
      pts.push([x, y0+yOff]);
    }
    const top = pts.map(p=>p.join(',')).join(' ');
    const bottom = `${x0+w},${y0+roughness*3} ${x0},${y0+roughness*3}`;
    return `M ${pts[0].join(',')} L ${top} L ${bottom} Z`;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Galling Mechanism — Asperity Contact · Adhesion · Material Transfer · Progressive Damage</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          {arrowDef('arME')}
          {arrowDef('arMEr','#7a2e00')}
        </defs>

        {/* ── Stage labels ── */}
        {['Stage 1: Initial Contact','Stage 2: Adhesion at Asperities','Stage 3: Material Transfer','Stage 4: Galling / Seizure'].map((label,i)=>(
          <text key={i} x={30+i*240} y={22} fontSize="10" fontWeight="700" fill="#7a2e00">{label}</text>
        ))}

        {/* ── Stage 1: Two rough surfaces approaching ── */}
        {(()=>{
          const ox=20, oy=60, w=200, gap=30;
          return <g>
            {/* Upper surface (moving) */}
            <path d={surfaceProfile(ox, oy, w, 12, 20, '#d4874a', '#7a2e00')}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Lower surface (stationary) */}
            <path d={surfaceProfile(ox, oy+gap+8, w, 10, 20, '#e8b07a', '#7a2e00')}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Motion arrow */}
            <line x1={ox+w+8} y1={oy+8} x2={ox+w+44} y2={oy+8}
              stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arME)"/>
            <text x={ox+w+50} y={oy+12} fontSize="9.5" fontWeight="700" fill="#c45a10">Sliding</text>
            {/* Normal force arrow */}
            <line x1={ox+w/2} y1={oy-30} x2={ox+w/2} y2={oy+2}
              stroke="#7a2e00" strokeWidth="2.5" markerEnd="url(#arMEr)"/>
            <text x={ox+w/2} y={oy-34} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">F_N</text>
            {/* Annotation */}
            <rect x={ox} y={oy+gap+30} width={w} height={28} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+w/2} y={oy+gap+43} textAnchor="middle" fontSize="9" fill="#7a2e00" fontWeight="700">Rough surfaces, no lubricant</text>
            <text x={ox+w/2} y={oy+gap+56} textAnchor="middle" fontSize="8.5" fill="#a04010">Asperities about to contact</text>
          </g>;
        })()}

        {/* Divider */}
        <line x1={235} y1={30} x2={235} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Stage 2: Adhesion — asperities weld together ── */}
        {(()=>{
          const ox=250, oy=60, w=200, gap=22;
          return <g>
            {/* Upper */}
            <path d={surfaceProfile(ox, oy, w, 12, 20, '#d4874a', '#7a2e00')}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Lower */}
            <path d={surfaceProfile(ox, oy+gap, w, 10, 20, '#e8b07a', '#7a2e00')}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Adhesion junctions (cold welds) — red spots at contact points */}
            {[0.18, 0.42, 0.67, 0.85].map((frac,i)=>(
              <g key={i}>
                <ellipse cx={ox+frac*w} cy={oy+gap-2} rx={8} ry={5}
                  fill="#8a1010" stroke="#5a0000" strokeWidth="1.5" opacity="0.9"/>
                <text x={ox+frac*w} y={oy+gap+12} textAnchor="middle" fontSize="7" fill="#8a1010">weld</text>
              </g>
            ))}
            <line x1={ox+w+8} y1={oy+8} x2={ox+w+44} y2={oy+8}
              stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arME)"/>
            <rect x={ox} y={oy+gap+24} width={w} height={28} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+w/2} y={oy+gap+37} textAnchor="middle" fontSize="9" fill="#7a2e00" fontWeight="700">Adhesive micro-junctions form</text>
            <text x={ox+w/2} y={oy+gap+50} textAnchor="middle" fontSize="8.5" fill="#a04010">σ_contact &gt; σ_yield locally</text>
            {/* Zoom inset */}
            <rect x={ox+60} y={oy+gap+58} width={80} height={50} rx={3}
              fill="#fdf0e4" stroke="#8a1010" strokeWidth="1.5"/>
            <text x={ox+100} y={oy+gap+72} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#8a1010">Cold weld junction</text>
            <text x={ox+100} y={oy+gap+84} textAnchor="middle" fontSize="8" fill="#a04010">actual contact area</text>
            <text x={ox+100} y={oy+gap+96} textAnchor="middle" fontSize="8" fill="#a04010">≪ nominal area</text>
          </g>;
        })()}

        <line x1={470} y1={30} x2={470} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Stage 3: Material transfer ── */}
        {(()=>{
          const ox=485, oy=60, w=200, gap=26;
          return <g>
            {/* Upper surface — with torn chunk */}
            <path d={surfaceProfile(ox, oy, w, 12, 20, '#d4874a', '#7a2e00')}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Lower surface with raised lumps (transferred material) */}
            <path d={surfaceProfile(ox, oy+gap, w, 10, 20, '#e8b07a', '#7a2e00')}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Transferred lumps on lower surface */}
            {[0.25, 0.60].map((frac,i)=>(
              <ellipse key={i} cx={ox+frac*w} cy={oy+gap-4} rx={12} ry={8}
                fill="#8a1010" stroke="#5a0000" strokeWidth="1.5"/>
            ))}
            {/* Torn crater on upper surface */}
            <ellipse cx={ox+0.25*w} cy={oy+8} rx={10} ry={5}
              fill="#5a1e00" stroke="#3a0e00" strokeWidth="1.5"/>
            {/* Transfer arrows */}
            <line x1={ox+0.25*w} y1={oy+10} x2={ox+0.25*w} y2={oy+gap-10}
              stroke="#8a1010" strokeWidth="2" strokeDasharray="3,2" markerEnd="url(#arME)"/>
            <line x1={ox+w+8} y1={oy+8} x2={ox+w+44} y2={oy+8}
              stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arME)"/>
            <rect x={ox} y={oy+gap+20} width={w} height={28} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+w/2} y={oy+gap+33} textAnchor="middle" fontSize="9" fill="#7a2e00" fontWeight="700">Material torn from surface</text>
            <text x={ox+w/2} y={oy+gap+46} textAnchor="middle" fontSize="8.5" fill="#a04010">Transferred to opposing surface</text>
            <rect x={ox+20} y={oy+gap+54} width={160} height={40} rx={3} fill="#fdf0e4" stroke="#8a1010" strokeWidth="1.2"/>
            <text x={ox+100} y={oy+gap+68} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#8a1010">Crater (donor surface)</text>
            <text x={ox+100} y={oy+gap+80} textAnchor="middle" fontSize="8" fill="#a04010">Lump (acceptor surface)</text>
            <text x={ox+100} y={oy+gap+92} textAnchor="middle" fontSize="8" fill="#a04010">→ abrasive third body</text>
          </g>;
        })()}

        <line x1={710} y1={30} x2={710} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Stage 4: Full galling / seizure ── */}
        {(()=>{
          const ox=725, oy=60, w=220;
          return <g>
            {/* Severely damaged surfaces */}
            {/* Upper — heavily scratched */}
            <rect x={ox} y={oy} width={w} height={28} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
            {[0.1,0.2,0.35,0.5,0.65,0.75,0.88].map((f,i)=>(
              <line key={i} x1={ox+f*w} y1={oy} x2={ox+f*w+6} y2={oy+28}
                stroke="#7a2e00" strokeWidth="2.5" opacity="0.5"/>
            ))}
            {/* Large transferred lumps */}
            {[0.2,0.45,0.7].map((f,i)=>(
              <ellipse key={i} cx={ox+f*w} cy={oy+42} rx={16} ry={11}
                fill="#8a1010" stroke="#5a0000" strokeWidth="2"/>
            ))}
            {/* Lower — grooved */}
            <rect x={ox} y={oy+54} width={w} height={28} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            {[0.15,0.28,0.42,0.58,0.72,0.85].map((f,i)=>(
              <rect key={i} x={ox+f*w-3} y={oy+54} width={6} height={28}
                fill="#7a2e00" opacity="0.35"/>
            ))}
            {/* SEIZED label */}
            <rect x={ox+w/2-50} y={oy+94} width={100} height={26} rx={4}
              fill="#8a1010" stroke="#5a0000" strokeWidth="2"/>
            <text x={ox+w/2} y={oy+111} textAnchor="middle" fontSize="12" fontWeight="700" fill="#fdf4ec">SEIZED</text>
            <rect x={ox} y={oy+130} width={w} height={40} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+w/2} y={oy+145} textAnchor="middle" fontSize="9" fill="#7a2e00" fontWeight="700">Interlocking lumps resist motion</text>
            <text x={ox+w/2} y={oy+158} textAnchor="middle" fontSize="8.5" fill="#a04010">Torque/force → component fracture</text>
          </g>;
        })()}

        {/* Bottom: galling vs wear comparison note */}
        <rect x={10} y={H-56} width={W-20} height={48} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={W/2} y={H-38} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">
          Galling vs. Wear: Galling is a form of severe adhesive wear — distinguished by MACRO-scale material transfer and surface damage visible to the naked eye
        </text>
        <text x={W/2} y={H-20} textAnchor="middle" fontSize="9.5" fill="#a04010">
          Unlike abrasive wear (gradual material removal), galling produces raised lumps, torn craters, and can cause catastrophic seizure after only a few contact cycles
        </text>
      </svg>
      <div style={S.dCaption}>Galling initiates when asperity contact stresses exceed the material yield strength — adhesion forms micro-welds that are stronger than the surrounding base material, causing tearing rather than sliding</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Galling resistance matrix: material combinations
// ─────────────────────────────────────────────────────────────────────────────
function DiagramMaterialMatrix() {
  const W=980, H=400;

  const materials = ['SS 304','SS 316','SS 17-4PH','Al 6061','Ti-6Al-4V','Mild Steel','Tool Steel','Bronze','PTFE-filled','Nitrided Steel'];
  const N = materials.length;

  // Galling resistance rating: 0=severe, 1=high risk, 2=moderate, 3=low risk, 4=resistant
  // Self-mated same materials are the worst (0)
  const rating = (i,j) => {
    if(i===j) {
      // Self-mated: stainless/Ti worst, tool steel better
      if(i<=1) return 0;    // SS self-mated: severe
      if(i===4) return 0;   // Ti self-mated: severe
      if(i===2) return 1;   // 17-4PH self-mated: high risk
      if(i===3) return 1;   // Al self-mated: high risk
      if(i===5) return 2;   // mild steel self: moderate
      if(i===6) return 3;   // tool steel self: low
      if(i===7) return 3;   // bronze self: low
      if(i===8) return 4;   // PTFE: resistant
      if(i===9) return 3;   // nitrided self: low
    }
    // Mixed pairs
    const pair = [i,j].sort().join('-');
    const map = {
      '0-1':1, '0-2':2, '0-3':2, '0-4':0, '0-5':2, '0-6':3, '0-7':3, '0-8':4, '0-9':3,
      '1-2':2, '1-3':2, '1-4':1, '1-5':2, '1-6':3, '1-7':3, '1-8':4, '1-9':3,
      '2-3':2, '2-4':1, '2-5':3, '2-6':3, '2-7':3, '2-8':4, '2-9':3,
      '3-4':1, '3-5':2, '3-6':3, '3-7':3, '3-8':4, '3-9':3,
      '4-5':1, '4-6':2, '4-7':2, '4-8':4, '4-9':2,
      '5-6':2, '5-7':3, '5-8':4, '5-9':3,
      '6-7':3, '6-8':4, '6-9':3,
      '7-8':4, '7-9':3,
      '8-9':4,
    };
    return map[pair] ?? 2;
  };

  const colors = ['#8a1010','#c45a10','#c4a010','#5a7820','#2a6020'];
  const labels = ['Severe','High risk','Moderate','Low risk','Resistant'];

  const cellW=74, cellH=30;
  const originX=130, originY=50;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Galling Resistance Matrix — Common Engineering Material Pairs (Unlubricated, Sliding Contact)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Column headers */}
        {materials.map((m,i)=>(
          <text key={i}
            x={originX + i*cellW + cellW/2}
            y={originY-8}
            textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00"
            transform={`rotate(-35,${originX+i*cellW+cellW/2},${originY-8})`}>{m}</text>
        ))}

        {/* Row headers */}
        {materials.map((m,i)=>(
          <text key={i}
            x={originX-8}
            y={originY+i*cellH+cellH/2+4}
            textAnchor="end" fontSize="8.5" fontWeight="700" fill="#7a2e00">{m}</text>
        ))}

        {/* Cells */}
        {materials.map((_,i)=>
          materials.map((_,j)=>{
            if(j>i) return null; // upper triangle only → mirror
            const r=rating(i,j);
            const cx=originX+j*cellW, cy=originY+i*cellH;
            return (
              <g key={`${i}-${j}`}>
                <rect x={cx+1} y={cy+1} width={cellW-2} height={cellH-2} rx={2}
                  fill={colors[r]} opacity={i===j?1:0.75}
                  stroke={i===j?'#1a0e06':'none'} strokeWidth={i===j?1.5:0}/>
                <text x={cx+cellW/2} y={cy+cellH/2+4} textAnchor="middle"
                  fontSize="7.5" fontWeight="700" fill="#fdf4ec">
                  {['S','H','M','L','R'][r]}
                </text>
              </g>
            );
          })
        )}

        {/* Legend */}
        <text x={originX+N*cellW+16} y={originY+14} fontSize="11" fontWeight="700" fill="#7a2e00">Legend</text>
        {colors.map((c,i)=>(
          <g key={i}>
            <rect x={originX+N*cellW+16} y={originY+24+i*32} width={20} height={20} rx={3} fill={c}/>
            <text x={originX+N*cellW+42} y={originY+38+i*32} fontSize="9.5" fill="#7a2e00" fontWeight="700">{labels[i]}</text>
          </g>
        ))}
        <text x={originX+N*cellW+16} y={originY+200} fontSize="9" fill="#a04010">Diagonal = self-mated</text>
        <text x={originX+N*cellW+16} y={originY+214} fontSize="9" fill="#a04010">(same material pair)</text>

        {/* Key observations */}
        <rect x={originX+N*cellW+14} y={originY+230} width={195} height={130} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={originX+N*cellW+22} y={originY+246} fontSize="9" fontWeight="700" fill="#7a2e00">Key observations:</text>
        {['SS self-mated: WORST','Ti self-mated: WORST','Bronze vs SS: Good','PTFE-filled: Best','Dissimilar metals','  usually better','Hardness differ-','  ence helps'].map((l,i)=>(
          <text key={i} x={originX+N*cellW+22} y={originY+260+i*13} fontSize="8.5"
            fill={l.startsWith(' ')?'#a04010':'#7a2e00'}>{l}</text>
        ))}
      </svg>
      <div style={S.dCaption}>Austenitic stainless steels (304, 316) paired with themselves produce the most severe galling — the same face-centred-cubic crystal structure and high ductility promotes adhesion; dissimilar material pairs almost always perform better</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Contact pressure vs sliding speed: galling threshold map
// ─────────────────────────────────────────────────────────────────────────────
function DiagramThresholdMap() {
  const W=980, H=400;
  const chartX=70, chartY=20, chartW=480, chartH=270;
  const baseY=chartY+chartH;

  // Log-log axes: x=sliding speed (mm/s), y=contact pressure (MPa)
  const xMin=0.01, xMax=1000, yMin=1, yMax=500;

  function lx(v) { return chartX + Math.log10(v/xMin)/Math.log10(xMax/xMin)*chartW; }
  function ly(v) { return baseY  - Math.log10(v/yMin)/Math.log10(yMax/yMin)*chartH; }

  // Galling threshold curves for three material/condition combinations
  // Points: [speed, pressure] on the galling boundary
  const boundaries = [
    { label:'SS 304 (dry)', color:'#8a1010', pts:[[0.01,80],[0.1,60],[1,40],[10,25],[100,15],[1000,10]] },
    { label:'SS 316 + lubricant', color:'#c45a10', pts:[[0.01,200],[0.1,160],[1,120],[10,80],[100,50],[1000,30]] },
    { label:'Nitrided Steel (dry)', color:'#5a7820', pts:[[0.01,400],[0.1,320],[1,250],[10,180],[100,120],[1000,70]] },
  ];

  // Operating point examples
  const opPoints = [
    { label:'Bolt tightening',   speed:0.1,  pressure:180, safe:false },
    { label:'Valve stem',        speed:50,   pressure:30,  safe:true  },
    { label:'Thread engagement', speed:5,    pressure:80,  safe:false },
    { label:'ACME screw',        speed:100,  pressure:20,  safe:true  },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Galling Threshold Map — Contact Pressure vs. Sliding Speed · Safe / Unsafe Operating Zones</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrowDef('arTM')}</defs>

        {/* Chart background */}
        <rect x={chartX} y={chartY} width={chartW} height={chartH}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5" rx={4}/>

        {/* Grid */}
        {[0.01,0.1,1,10,100,1000].map(v=>(
          <g key={v}>
            <line x1={lx(v)} y1={chartY} x2={lx(v)} y2={baseY}
              stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={lx(v)} y={baseY+16} textAnchor="middle" fontSize="9" fill="#7a4a28">
              {v<1?v:v>=1000?'1k':v}
            </text>
          </g>
        ))}
        {[1,5,10,50,100,500].map(v=>{
          const y=ly(v); if(y<chartY) return null;
          return <g key={v}>
            <line x1={chartX} y1={y} x2={chartX+chartW} y2={y}
              stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={chartX-6} y={y+4} textAnchor="end" fontSize="9" fill="#7a4a28">{v}</text>
          </g>;
        })}

        {/* Axes */}
        <text x={chartX+chartW/2} y={baseY+32} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Sliding Speed (mm/s) [log]</text>
        <text x={chartX-50} y={chartY+chartH/2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,${chartX-50},${chartY+chartH/2})`}>Contact Pressure (MPa) [log]</text>

        {/* Shaded "GALLING" zone for SS 304 */}
        {(()=>{
          const b=boundaries[0];
          const topPts=b.pts.map(([s,p])=>`${lx(s).toFixed(1)},${ly(p).toFixed(1)}`).join(' ');
          const bottomLine=`${lx(1000).toFixed(1)},${baseY} ${lx(0.01).toFixed(1)},${baseY}`;
          return <path d={`M ${topPts} L ${bottomLine} Z`} fill="#8a1010" opacity="0.08"/>;
        })()}

        {/* Threshold curves */}
        {boundaries.map((b,bi)=>{
          const pathD='M '+b.pts.map(([s,p])=>`${lx(s).toFixed(1)},${ly(p).toFixed(1)}`).join(' L ');
          return <path key={bi} d={pathD} fill="none" stroke={b.color} strokeWidth="2.5" opacity="0.9"/>;
        })}

        {/* "GALLING" and "SAFE" zone labels — placed well away from curves */}
        <text x={lx(0.1)} y={ly(4)} textAnchor="middle" fontSize="13" fontWeight="700" fill="#8a1010" opacity="0.45">GALLING</text>
        <text x={lx(0.5)} y={ly(450)} textAnchor="middle" fontSize="13" fontWeight="700" fill="#5a7820" opacity="0.45">SAFE</text>

        {/* Operating points — label positions offset to avoid overlap with each other */}
        {opPoints.map((op,i)=>{
          // Custom offsets per point to avoid crowding
          const offsets=[[12,-10],[12,4],[12,4],[-90,4]];
          const [dx,dy]=offsets[i];
          return (
            <g key={i}>
              <circle cx={lx(op.speed)} cy={ly(op.pressure)} r={7}
                fill={op.safe?'#5a7820':'#8a1010'} stroke="#fdf4ec" strokeWidth="2"/>
              <text x={lx(op.speed)+dx} y={ly(op.pressure)+dy}
                fontSize="9" fill={op.safe?'#5a7820':'#8a1010'} fontWeight="700">{op.label}</text>
            </g>
          );
        })}

        {/* Legend — horizontal strip BELOW the x-axis label, outside chart */}
        <rect x={chartX} y={baseY+42} width={chartW} height={22} rx={3}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        {boundaries.map((b,i)=>(
          <g key={i}>
            <line x1={chartX+10+i*162} y1={baseY+53} x2={chartX+36+i*162} y2={baseY+53}
              stroke={b.color} strokeWidth="2.5"/>
            <text x={chartX+42+i*162} y={baseY+57}
              fontSize="9" fill={b.color} fontWeight="700">{b.label} (threshold)</text>
          </g>
        ))}

        {/* Right panel */}
        <rect x={582} y={20} width={388} height={H-32} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={776} y={40} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Threshold Factors</text>
        <line x1={590} y1={48} x2={962} y2={48} stroke="#e8b07a"/>
        {[
          ['Galling threshold:',  'Minimum contact pressure at'],
          ['',                    'which galling initiates at given'],
          ['',                    'sliding speed'],
          ['',                    ''],
          ['PV limit (similar):', 'P × V = constant for many'],
          ['',                    'material pairs (P=MPa, V=m/s)'],
          ['',                    ''],
          ['Speed effect:',       'Higher speed → lower threshold'],
          ['',                    '(more frictional heat, lower μ)'],
          ['',                    ''],
          ['Lubrication:',        'Raises threshold 2–10× by'],
          ['',                    'separating asperity contacts'],
          ['',                    ''],
          ['Surface finish:',     'Smoother = lower real area'],
          ['',                    'but TOO smooth can trap'],
          ['',                    'lubricant → optimum Ra exists'],
          ['',                    ''],
          ['Hardness:',           'Higher hardness raises threshold'],
          ['',                    'Hardness ratio matters more'],
          ['',                    'than absolute hardness alone'],
          ['',                    ''],
          ['Critical PV:',        'SS 304 dry ≈ 0.05–0.15 MPa·m/s'],
          ['',                    'Nitrided steel ≈ 1–5 MPa·m/s'],
          ['',                    'Bronze/SS ≈ 0.5–2 MPa·m/s'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={592} y={60+i*11} fontSize="9.5" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?716:598} y={60+i*11} fontSize="9.5" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The galling threshold is strongly speed-dependent — at low sliding speeds (bolt tightening, slow threads) very high contact pressures are needed to initiate galling; at high speeds thermal softening dramatically lowers the threshold</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Surface finish and hardness effects: Ra vs galling risk
// ─────────────────────────────────────────────────────────────────────────────
function DiagramSurfaceHardness() {
  const W=980, H=340;

  // Left chart: galling risk vs Ra for SS 304 (U-shaped — too smooth traps lubricant, too rough = high asperities)
  const chartX=55, chartY=20, chartW=380, chartH=250;
  const baseY=chartY+chartH;
  const Ra_pts=[0.1,0.2,0.4,0.8,1.6,3.2,6.3,12.5]; // μm
  // Galling risk index (0=low, 100=high)
  const risk_dry  =[65,45,30,25,30,45,65,85];
  const risk_lub  =[85,55,25,15,18,28,45,70];

  const maxRa=12.5;
  function raX(ra) { return chartX + (ra/maxRa)*chartW; }
  function riskY(r) { return baseY - (r/100)*chartH; }

  const dryPath  ='M '+Ra_pts.map((r,i)=>`${raX(r).toFixed(1)},${riskY(risk_dry[i]).toFixed(1)}`).join(' L ');
  const lubPath  ='M '+Ra_pts.map((r,i)=>`${raX(r).toFixed(1)},${riskY(risk_lub[i]).toFixed(1)}`).join(' L ');

  // Right: hardness bar chart
  const hardnessPairs=[
    { label:'SS 304 / SS 304',  h1:160, h2:160, risk:95, color:'#8a1010' },
    { label:'SS 304 / Bronze',  h1:160, h2:80,  risk:30, color:'#c45a10' },
    { label:'Steel / Bronze',   h1:280, h2:80,  risk:18, color:'#c4a010' },
    { label:'Nitrided / SS',    h1:700, h2:160, risk:12, color:'#5a7820' },
    { label:'Tool St / Bronze', h1:600, h2:80,  risk:8,  color:'#2a6020' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Surface Finish &amp; Hardness Effects — Ra vs. Galling Risk · Hardness Mismatch Benefit</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrowDef('arSH')}</defs>

        {/* Left: Ra chart */}
        <rect x={chartX} y={chartY} width={chartW} height={chartH}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5" rx={4}/>
        {[0,25,50,75,100].map(v=>{
          const y=riskY(v);
          return <g key={v}>
            <line x1={chartX} y1={y} x2={chartX+chartW} y2={y}
              stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={chartX-6} y={y+4} textAnchor="end" fontSize="9" fill="#7a4a28">{v}</text>
          </g>;
        })}
        {Ra_pts.map(r=>(
          <text key={r} x={raX(r)} y={baseY+15} textAnchor="middle" fontSize="8.5" fill="#7a4a28">{r}</text>
        ))}
        <text x={chartX+chartW/2} y={baseY+30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Surface Roughness Ra (μm)</text>
        <text x={chartX-44} y={chartY+chartH/2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,${chartX-44},${chartY+chartH/2})`}>Galling Risk Index</text>

        <path d={dryPath} fill="none" stroke="#8a1010" strokeWidth="2.5"/>
        <path d={lubPath} fill="none" stroke="#5a7820" strokeWidth="2.5" strokeDasharray="7,4"/>

        {/* Optimal Ra zone */}
        <rect x={raX(0.8)-4} y={chartY} width={raX(1.6)-raX(0.8)+8} height={chartH}
          fill="#5a7820" opacity="0.08"/>
        <text x={(raX(0.8)+raX(1.6))/2} y={chartY+20} textAnchor="middle" fontSize="9" fontWeight="700" fill="#5a7820">Optimal</text>
        <text x={(raX(0.8)+raX(1.6))/2} y={chartY+33} textAnchor="middle" fontSize="9" fill="#5a7820">Ra zone</text>

        <line x1={chartX+14} y1={chartY+56} x2={chartX+44} y2={chartY+56} stroke="#8a1010" strokeWidth="2.5"/>
        <text x={chartX+50} y={chartY+60} fontSize="9.5" fill="#8a1010" fontWeight="700">Dry contact (SS 304)</text>
        <line x1={chartX+14} y1={chartY+72} x2={chartX+44} y2={chartY+72} stroke="#5a7820" strokeWidth="2.5" strokeDasharray="7,4"/>
        <text x={chartX+50} y={chartY+76} fontSize="9.5" fill="#5a7820" fontWeight="700">With lubricant</text>

        <line x1={470} y1={16} x2={470} y2={H-16} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* Right: hardness mismatch effect */}
        <text x={720} y={26} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Hardness Mismatch Effect on Galling Risk</text>
        {hardnessPairs.map((p,i)=>{
          const barY=50+i*52;
          const maxRisk=100;
          const barW=p.risk*2.8;
          return (
            <g key={i}>
              <text x={486} y={barY+18} fontSize="9.5" fontWeight="700" fill="#7a2e00">{p.label}</text>
              <text x={486} y={barY+30} fontSize="8.5" fill="#a04010">
                HV: {p.h1} vs {p.h2}  (Δ={Math.abs(p.h1-p.h2)})
              </text>
              {/* Risk bar */}
              <rect x={486} y={barY+34} width={barW} height={14} rx={3}
                fill={p.color} stroke="#7a2e00" strokeWidth="1.2" opacity="0.88"/>
              <text x={486+barW+6} y={barY+46} fontSize="9.5" fontWeight="700" fill={p.color}>
                Risk: {p.risk}%
              </text>
            </g>
          );
        })}
        <text x={486} y={315} fontSize="9" fill="#7a2e00" fontStyle="italic">
          ↑ Greater hardness difference → lower galling risk
        </text>
      </svg>
      <div style={S.dCaption}>An optimum surface finish of Ra 0.8–1.6 μm minimises galling — too smooth removes the micro-valleys that retain lubricant, while too rough creates high asperities that puncture the lubricant film</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Lubrication and coating strategies comparison
// ─────────────────────────────────────────────────────────────────────────────
function DiagramLubrication() {
  const W=980, H=370;

  const strategies = [
    { title:'No Lubrication\n(dry)',
      color:'#8a1010',
      risk:'Extreme',
      desc:'Real contact area = asperities\nNo film to prevent adhesion\nOnly material hardness & mismatch help',
      schematic: (cx,cy)=><>
        {/* Two rough surfaces in contact - no gap */}
        <rect x={cx-60} y={cy-28} width={120} height={22} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {[-.4,-.15,.1,.35].map((f,i)=><rect key={i} x={cx+f*100} y={cy-6} width={10} height={8} fill="#8a1010"/>)}
        <rect x={cx-60} y={cy+2}  width={120} height={22} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        {[-.35,-.1,.15,.4].map((f,i)=><rect key={i} x={cx+f*100} y={cy+2} width={10} height={8} fill="#a04010"/>)}
        <text x={cx} y={cy+36} textAnchor="middle" fontSize="9" fontWeight="700" fill="#8a1010">CONTACT</text>
      </> },
    { title:'Liquid Lubricant\n(EHL / BL)',
      color:'#c45a10',
      risk:'Low–Moderate',
      desc:'Elastohydrodynamic or boundary film\nReduces real contact area\nFilm must withstand contact pressure',
      schematic: (cx,cy)=><>
        <rect x={cx-60} y={cy-30} width={120} height={20} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Oil film */}
        <rect x={cx-58} y={cy-8} width={116} height={12} rx={2} fill="#c4a010" opacity="0.6"/>
        <text x={cx} y={cy+1} textAnchor="middle" fontSize="8" fill="#7a4a28">oil film</text>
        <rect x={cx-60} y={cy+6} width={120} height={20} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx} y={cy+36} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">SEPARATED</text>
      </> },
    { title:'Solid Lubricant\n(MoS₂, PTFE, WS₂)',
      color:'#5a7820',
      risk:'Low',
      desc:'Lamellar structure slides easily\nWorks in vacuum / extreme T\nNo re-application needed (limited life)',
      schematic: (cx,cy)=><>
        <rect x={cx-60} y={cy-30} width={120} height={20} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* MoS2 coating layers */}
        {[0,3,6].map(dy=><rect key={dy} x={cx-56} y={cy-8+dy} width={112} height={2} fill="#5a7820" opacity="0.7"/>)}
        <text x={cx} y={cy+2} textAnchor="middle" fontSize="8" fill="#5a7820">MoS₂ coating</text>
        <rect x={cx-60} y={cy+8} width={120} height={20} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx} y={cy+36} textAnchor="middle" fontSize="9" fontWeight="700" fill="#5a7820">COATED</text>
      </> },
    { title:'Surface Coating\n(TiN, CrN, DLC)',
      color:'#2a6020',
      risk:'Very Low',
      desc:'Hard ceramic layer prevents adhesion\nDLC: very low μ ≈ 0.05–0.15\nLimited thickness (2–10 μm PVD)',
      schematic: (cx,cy)=><>
        <rect x={cx-60} y={cy-30} width={120} height={18} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Hard coating */}
        <rect x={cx-60} y={cy-12} width={120} height={5} rx={1} fill="#2a6020" stroke="#1a4010" strokeWidth="1"/>
        <text x={cx+66} y={cy-8} fontSize="8" fill="#2a6020">TiN</text>
        <rect x={cx-60} y={cy-3} width={120} height={5} rx={1} fill="#2a6020" stroke="#1a4010" strokeWidth="1"/>
        <text x={cx} y={cy+10} textAnchor="middle" fontSize="7.5" fill="#a04010">2–10 μm</text>
        <rect x={cx-60} y={cy+14} width={120} height={18} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx} y={cy+42} textAnchor="middle" fontSize="9" fontWeight="700" fill="#2a6020">PROTECTED</text>
      </> },
  ];

  const colW=W/strategies.length;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Lubrication &amp; Surface Treatment Strategies — Mechanism, Risk Level, and Applicability</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {strategies.map((st,i)=>{
          const cx=colW*i+colW/2;
          const headerColor=st.color;
          return (
            <g key={i}>
              {/* Card */}
              <rect x={colW*i+4} y={4} width={colW-8} height={H-8} rx={6}
                fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke={headerColor} strokeWidth="1.8"/>
              <rect x={colW*i+4} y={4}  width={colW-8} height={38} rx={6} fill={headerColor} stroke="none"/>
              <rect x={colW*i+4} y={32} width={colW-8} height={10}  fill={headerColor} stroke="none"/>
              {st.title.split('\n').map((l,j)=>(
                <text key={j} x={cx} y={20+j*16} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fdf4ec">{l}</text>
              ))}
              {/* Schematic */}
              {st.schematic(cx, 112)}
              {/* Risk badge */}
              <rect x={cx-50} y={162} width={100} height={22} rx={4} fill={headerColor} opacity="0.9"/>
              <text x={cx} y={177} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">Risk: {st.risk}</text>
              {/* Description */}
              {st.desc.split('\n').map((l,j)=>(
                <text key={j} x={colW*i+16} y={200+j*17} fontSize="9" fill="#7a2e00">{l}</text>
              ))}
            </g>
          );
        })}

        {/* Bottom comparison bar */}
        <rect x={10} y={H-42} width={W-20} height={34} rx={4} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        <text x={W/2} y={H-26} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">
          Galling resistance:  Dry &lt; Liquid lubricant &lt; Solid lubricant (MoS₂) &lt; Hard coating (TiN/DLC) ≈ Material change
        </text>
        <text x={W/2} y={H-11} textAnchor="middle" fontSize="9.5" fill="#a04010">
          For critical joints: combine hard coating + solid lubricant + optimised surface finish for best protection
        </text>
      </svg>
      <div style={S.dCaption}>Solid lubricants (MoS₂, WS₂) are preferred for vacuum, extreme temperature, or food-grade applications where liquid lubricants are impractical; DLC coatings provide the lowest friction coefficient achievable with a solid surface treatment</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Common galling failure scenarios: bolt, valve, thread, bearing
// ─────────────────────────────────────────────────────────────────────────────
function DiagramFailureScenarios() {
  const W=980, H=340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Galling Failure Scenarios — Bolted Joints · Threads · Valve Stems · Sliding Surfaces</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrowDef('arFS')}</defs>

        {/* ── Scenario 1: Bolt galling ── */}
        {(()=>{
          const ox=40, oy=40;
          return <g>
            <text x={ox+80} y={oy-4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">1. Bolt Tightening</text>
            {/* Hex head */}
            <polygon points={`${ox+80},${oy} ${ox+108},${oy+16} ${ox+108},${oy+48} ${ox+80},${oy+64} ${ox+52},${oy+48} ${ox+52},${oy+16}`}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
            <text x={ox+80} y={oy+36} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">BOLT</text>
            {/* Shank */}
            <rect x={ox+66} y={oy+64} width={28} height={80} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Thread galling zone */}
            <rect x={ox+62} y={oy+104} width={36} height={20} fill="#8a1010" opacity="0.6" rx={2}/>
            <text x={ox+100} y={oy+117} fontSize="8.5" fill="#8a1010" fontWeight="700">Galling zone</text>
            {/* Nut */}
            <rect x={ox+56} y={oy+144} width={48} height={30} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            <text x={ox+80} y={oy+163} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">NUT</text>
            {/* Torque arrow */}
            <path d={`M ${ox+108} ${oy+36} A 36 36 0 0 1 ${ox+80} ${oy}`}
              fill="none" stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arFS)"/>
            <text x={ox+122} y={oy+40} fontSize="9" fill="#c45a10">T</text>
            {/* Note */}
            <rect x={ox} y={oy+200} width={160} height={42} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+80} y={oy+215} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">SS bolts in SS tapped holes</text>
            <text x={ox+80} y={oy+227} textAnchor="middle" fontSize="8.5" fill="#a04010">Most common galling failure</text>
            <text x={ox+80} y={oy+239} textAnchor="middle" fontSize="8.5" fill="#a04010">Bolt seizes before full torque</text>
          </g>;
        })()}

        <line x1={240} y1={20} x2={240} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Scenario 2: Thread engagement ── */}
        {(()=>{
          const ox=260, oy=40;
          // Draw thread profile
          const threadH=130, threadW=140;
          return <g>
            <text x={ox+70} y={oy-4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">2. Threaded Rod / Nut</text>
            {/* External thread (rod) */}
            <rect x={ox+20} y={oy} width={100} height={threadH} rx={3}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Thread grooves */}
            {Array.from({length:9},(_,i)=>(
              <rect key={i} x={ox+18} y={oy+6+i*14} width={104} height={6} rx={1}
                fill="#fdf0e4" stroke="none"/>
            ))}
            <text x={ox+70} y={oy+threadH/2+4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">ROD</text>
            {/* Internal thread (nut) — partial view */}
            <rect x={ox+6} y={oy+30} width={14} height={70} rx={2}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            <rect x={ox+120} y={oy+30} width={14} height={70} rx={2}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Galling spots on thread flanks */}
            {[0,1,2,3].map(i=>(
              <ellipse key={i} cx={ox+20} cy={oy+40+i*18} rx={5} ry={3}
                fill="#8a1010" opacity="0.8"/>
            ))}
            <rect x={ox} y={oy+threadH+14} width={140} height={42} rx={3}
              fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+70} y={oy+threadH+29} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Galling on thread flanks</text>
            <text x={ox+70} y={oy+threadH+41} textAnchor="middle" fontSize="8.5" fill="#a04010">High contact pressure on</text>
            <text x={ox+70} y={oy+threadH+53} textAnchor="middle" fontSize="8.5" fill="#a04010">60° thread flanks during make-up</text>
          </g>;
        })()}

        <line x1={490} y1={20} x2={490} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Scenario 3: Valve stem ── */}
        {(()=>{
          const ox=510, oy=40;
          return <g>
            <text x={ox+70} y={oy-4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">3. Valve Stem / Guide</text>
            {/* Valve guide (bore) */}
            <rect x={ox+30} y={oy} width={80} height={140} rx={4}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
            <rect x={ox+46} y={oy} width={48} height={140} fill="#fdf4ec" stroke="none"/>
            {/* Valve stem */}
            <rect x={ox+50} y={oy-20} width={40} height={160} rx={3}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            <text x={ox+70} y={oy+70} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">STEM</text>
            {/* Galling marks */}
            {[30,60,90,110].map((dy,i)=>(
              <line key={i} x1={ox+50} y1={oy+dy} x2={ox+62} y2={oy+dy+4}
                stroke="#8a1010" strokeWidth="2" opacity="0.8"/>
            ))}
            {/* Reciprocating arrow */}
            <line x1={ox+100} y1={oy+30} x2={ox+100} y2={oy+110}
              stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arFS)" markerStart="url(#arFS)"/>
            <rect x={ox} y={oy+155} width={140} height={55} rx={3}
              fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+70} y={oy+170} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Reciprocating stem contact</text>
            <text x={ox+70} y={oy+182} textAnchor="middle" fontSize="8.5" fill="#a04010">High temp + vibration + SS</text>
            <text x={ox+70} y={oy+194} textAnchor="middle" fontSize="8.5" fill="#a04010">material → classic galling site</text>
            <text x={ox+70} y={oy+206} textAnchor="middle" fontSize="8.5" fill="#a04010">Fix: Stellite hard-facing</text>
          </g>;
        })()}

        <line x1={740} y1={20} x2={740} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Scenario 4: Spline / sliding fit ── */}
        {(()=>{
          const ox=758, oy=40;
          const spokeN=8, r=52;
          const cx=ox+95, cy=oy+75;
          return <g>
            <text x={cx} y={oy-4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">4. Spline / Sliding Fit</text>
            {/* External spline */}
            <circle cx={cx} cy={cy} r={r+10} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            {Array.from({length:spokeN},(_,i)=>{
              const a=i*2*Math.PI/spokeN;
              return <g key={i}>
                <rect x={cx+(r+2)*Math.cos(a)-5} y={cy+(r+2)*Math.sin(a)-5} width={10} height={10}
                  fill="#c45a10" stroke="#7a2e00" strokeWidth="1"
                  transform={`rotate(${a*180/Math.PI+45},${cx+(r+2)*Math.cos(a)},${cy+(r+2)*Math.sin(a)})`}/>
              </g>;
            })}
            <circle cx={cx} cy={cy} r={28} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
            {/* Galling marks on teeth */}
            {[0,1,2,3].map(i=>{
              const a=i*Math.PI/2;
              return <ellipse key={i} cx={cx+(r+6)*Math.cos(a)} cy={cy+(r+6)*Math.sin(a)} rx={6} ry={3}
                fill="#8a1010" opacity="0.9"
                transform={`rotate(${a*180/Math.PI},${cx+(r+6)*Math.cos(a)},${cy+(r+6)*Math.sin(a)})`}/>;
            })}
            <rect x={ox} y={oy+155} width={190} height={56} rx={3}
              fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={cx} y={oy+170} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Spline tooth flanks</text>
            <text x={cx} y={oy+182} textAnchor="middle" fontSize="8.5" fill="#a04010">Axial sliding under torque</text>
            <text x={cx} y={oy+194} textAnchor="middle" fontSize="8.5" fill="#a04010">High HV mismatch required</text>
            <text x={cx} y={oy+206} textAnchor="middle" fontSize="8.5" fill="#a04010">Fix: case harden + MoS₂</text>
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>The four most common galling failure modes in practice — bolted joints account for the majority of reported galling incidents, particularly when stainless steel fasteners are used in stainless tapped holes without anti-seize compound</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Prevention flowchart: design decision tree
// ─────────────────────────────────────────────────────────────────────────────
function DiagramPreventionFlow() {
  const W=980, H=360;

  const nodes = [
    { id:'start', x:490, y:30,  w:180, h:38, label:'Sliding Contact Joint\nDesign / Redesign', color:'#d4874a', type:'start' },
    { id:'sameMat', x:490, y:110, w:220, h:38, label:'Same or similar material\nboth surfaces?', color:'#c45a10', type:'decision' },
    { id:'changeMat', x:180, y:190, w:180, h:38, label:'Change to dissimilar pair\nor harder material', color:'#5a7820', type:'action' },
    { id:'hardCheck', x:490, y:190, w:220, h:38, label:'Hardness difference\n≥ 100 HV?', color:'#c45a10', type:'decision' },
    { id:'addHard', x:760, y:190, w:180, h:38, label:'Add surface hardening\n(nitriding, carburising)', color:'#5a7820', type:'action' },
    { id:'lubCheck', x:490, y:270, w:220, h:38, label:'Lubricant practical?\n(temp, contamination)', color:'#c45a10', type:'decision' },
    { id:'addLub',  x:200, y:270, w:180, h:38, label:'Apply anti-seize or\nsolid lubricant (MoS₂)', color:'#5a7820', type:'action' },
    { id:'coat',    x:780, y:270, w:180, h:38, label:'Apply hard coating\n(TiN, DLC, CrN)', color:'#5a7820', type:'action' },
    { id:'finish',  x:490, y:340, w:220, h:28, label:'Optimise surface finish Ra 0.8–1.6 μm + verify torque / contact pressure', color:'#7a2e00', type:'end' },
  ];

  // Use simpler inline layout
  const bw=220, bh=40, gap=80;
  const steps = [
    { x:490, y:28,  label:'Sliding Contact Joint Design',     color:'#d4874a' },
    { x:490, y:118, label:'Q: Same or similar material?',      color:'#c45a10' },
    { x:490, y:208, label:'Q: Hardness difference ≥ 100 HV?', color:'#c45a10' },
    { x:490, y:298, label:'Q: Lubricant practical?',           color:'#c45a10' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Galling Prevention Decision Flow — Material · Hardness · Lubrication · Coating · Finish</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          {arrowDef('arPF')}
          {arrowDef('arPFg','#5a7820')}
        </defs>

        {/* Step 1 */}
        <rect x={380} y={14} width={220} height={44} rx={8} fill="#d4874a" stroke="#5a1e00" strokeWidth="2"/>
        <circle cx={393} cy={27} r={9} fill="#fdf0e4" stroke="#5a1e00" strokeWidth="1.5"/>
        <text x={393} y={31} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">1</text>
        <text x={490} y={32} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#fdf4ec">Sliding Contact Joint</text>
        <text x={490} y={47} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#fdf4ec">Design / Redesign</text>

        {/* Arrow down */}
        <line x1={490} y1={58} x2={490} y2={86} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arPF)"/>

        {/* Step 2: decision — same material? */}
        <rect x={370} y={88} width={240} height={44} rx={6} fill="#c45a10" stroke="#5a1e00" strokeWidth="2"/>
        <text x={490} y={107} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">Same or similar materials?</text>
        <text x={490} y={122} textAnchor="middle" fontSize="9.5" fill="#fdf4ec">(identical alloy both surfaces)</text>
        {/* YES → left */}
        <line x1={370} y1={110} x2={240} y2={110} stroke="#8a1010" strokeWidth="2" markerEnd="url(#arPF)"/>
        <text x={305} y={106} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#8a1010">YES →</text>
        <rect x={100} y={90} width={140} height={40} rx={5} fill="#5a7820" stroke="#3a5010" strokeWidth="1.8"/>
        <text x={170} y={108} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fdf4ec">Change to dissimilar</text>
        <text x={170} y={122} textAnchor="middle" fontSize="9" fill="#fdf4ec">material pair</text>
        {/* NO → down */}
        <line x1={490} y1={132} x2={490} y2={160} stroke="#5a7820" strokeWidth="2" markerEnd="url(#arPFg)"/>
        <text x={498} y={150} fontSize="9.5" fontWeight="700" fill="#5a7820">NO</text>

        {/* Step 3: hardness */}
        <rect x={370} y={162} width={240} height={44} rx={6} fill="#c45a10" stroke="#5a1e00" strokeWidth="2"/>
        <text x={490} y={181} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">Hardness difference ≥ 100 HV?</text>
        <text x={490} y={196} textAnchor="middle" fontSize="9.5" fill="#fdf4ec">(between mating surfaces)</text>
        {/* NO → right */}
        <line x1={610} y1={184} x2={740} y2={184} stroke="#8a1010" strokeWidth="2" markerEnd="url(#arPF)"/>
        <text x={675} y={180} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#8a1010">NO →</text>
        <rect x={740} y={164} width={160} height={40} rx={5} fill="#5a7820" stroke="#3a5010" strokeWidth="1.8"/>
        <text x={820} y={182} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fdf4ec">Add surface hardening</text>
        <text x={820} y={196} textAnchor="middle" fontSize="9" fill="#fdf4ec">nitriding / carburising</text>
        {/* YES → down */}
        <line x1={490} y1={206} x2={490} y2={234} stroke="#5a7820" strokeWidth="2" markerEnd="url(#arPFg)"/>
        <text x={498} y={224} fontSize="9.5" fontWeight="700" fill="#5a7820">YES</text>

        {/* Step 4: lubrication */}
        <rect x={370} y={236} width={240} height={44} rx={6} fill="#c45a10" stroke="#5a1e00" strokeWidth="2"/>
        <text x={490} y={255} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">Lubricant practical?</text>
        <text x={490} y={270} textAnchor="middle" fontSize="9.5" fill="#fdf4ec">(temp range, contamination ok)</text>
        {/* YES → left */}
        <line x1={370} y1={258} x2={240} y2={258} stroke="#5a7820" strokeWidth="2" markerEnd="url(#arPFg)"/>
        <text x={305} y={254} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#5a7820">YES →</text>
        <rect x={100} y={238} width={140} height={40} rx={5} fill="#5a7820" stroke="#3a5010" strokeWidth="1.8"/>
        <text x={170} y={256} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fdf4ec">Anti-seize compound</text>
        <text x={170} y={270} textAnchor="middle" fontSize="9" fill="#fdf4ec">or MoS₂ / PTFE</text>
        {/* NO → right */}
        <line x1={610} y1={258} x2={740} y2={258} stroke="#8a1010" strokeWidth="2" markerEnd="url(#arPF)"/>
        <text x={675} y={254} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#8a1010">NO →</text>
        <rect x={740} y={238} width={160} height={40} rx={5} fill="#5a7820" stroke="#3a5010" strokeWidth="1.8"/>
        <text x={820} y={256} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fdf4ec">Hard coating</text>
        <text x={820} y={270} textAnchor="middle" fontSize="9" fill="#fdf4ec">TiN / DLC / CrN (PVD)</text>

        {/* Final step */}
        <line x1={490} y1={280} x2={490} y2={306} stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arPF)"/>
        <rect x={310} y={308} width={360} height={40} rx={6} fill="#7a2e00" stroke="#5a1e00" strokeWidth="2"/>
        <text x={490} y={326} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">Optimise surface finish Ra 0.8–1.6 μm</text>
        <text x={490} y={340} textAnchor="middle" fontSize="9.5" fill="#fdf4ec">Verify contact pressure &lt; PV limit · Torque spec + anti-seize if bolted</text>
      </svg>
      <div style={S.dCaption}>Galling prevention is most effective when addressed at the design stage — selecting dissimilar materials with adequate hardness difference eliminates the root cause; lubrication and coatings are secondary mitigations</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Testing methods: button-on-block, pin-on-disk, scratch test
// ─────────────────────────────────────────────────────────────────────────────
function DiagramTesting() {
  const W=980, H=360;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Galling Test Methods — Button-on-Block · Pin-on-Disc · Scratch Test · Field Inspection</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrowDef('arTE')}</defs>

        {/* ── Test 1: Button-on-block (ASTM G98) ── */}
        {(()=>{
          const ox=30, oy=90;  // oy=90 so title at oy-32=58 is well below SVG top=24
          // Layout from top: title(58), gap, LoadF label(72), load arrow(76→88), button(90→110), block(118→146), base(146→156)
          // Rotation arc is shown to the RIGHT side of button+block stack
          const btnCX = ox+90, btnTop = oy, btnBot = oy+20;
          const blockTop = oy+28, blockBot = oy+56;
          const rotCX = ox+155;  // centre x for rotation arc — to the right of the button
          const rotCY = (btnTop+blockBot)/2;  // vertically centred on button+block midpoint

          return <g>
            {/* Title */}
            <text x={ox+90} y={oy-32} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Button-on-Block (ASTM G98)</text>

            {/* Load F — label above arrow, both above button with clear gap */}
            <text x={ox+90} y={oy-18} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Load F ↓</text>
            <line x1={ox+90} y1={oy-12} x2={ox+90} y2={oy-2}
              stroke="#7a2e00" strokeWidth="2.5" markerEnd="url(#arTE)"/>

            {/* Button body — flat-bottomed cylinder shape */}
            <ellipse cx={btnCX} cy={btnTop} rx={30} ry={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
            <rect x={btnCX-30} y={btnTop} width={60} height={20} fill="#c45a10" stroke="none"/>
            <line x1={btnCX-30} y1={btnTop}    x2={btnCX-30} y2={btnBot} stroke="#7a2e00" strokeWidth="2"/>
            <line x1={btnCX+30} y1={btnTop}    x2={btnCX+30} y2={btnBot} stroke="#7a2e00" strokeWidth="2"/>
            <ellipse cx={btnCX} cy={btnBot} rx={30} ry={10} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
            <text x={btnCX} y={btnTop+14} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">BUTTON</text>

            {/* Rotation arc — to the RIGHT of the button, pointing clockwise */}
            <path d={`M ${rotCX} ${btnTop-4} A 18 18 0 1 1 ${rotCX-1} ${btnTop-4}`}
              fill="none" stroke="#c45a10" strokeWidth="2.2" markerEnd="url(#arTE)"/>
            <text x={rotCX+22} y={btnTop+10} fontSize="8.5" fill="#c45a10" fontWeight="700">1 turn</text>

            {/* Block */}
            <rect x={ox+20} y={blockTop} width={140} height={28} rx={3}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            <text x={ox+90} y={blockTop+18} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">BLOCK (flat)</text>

            {/* Base plate */}
            <rect x={ox+10} y={blockBot} width={160} height={10} rx={2} fill="#7a2e00"/>

            {/* Description box */}
            <rect x={ox} y={blockBot+18} width={200} height={86} rx={3}
              fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+100} y={blockBot+33} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Procedure:</text>
            <text x={ox+100} y={blockBot+48} textAnchor="middle" fontSize="8.5" fill="#a04010">1. Apply load F (stepped)</text>
            <text x={ox+100} y={blockBot+61} textAnchor="middle" fontSize="8.5" fill="#a04010">2. Rotate button 1 revolution</text>
            <text x={ox+100} y={blockBot+74} textAnchor="middle" fontSize="8.5" fill="#a04010">3. Inspect for galling visually</text>
            <text x={ox+100} y={blockBot+87} textAnchor="middle" fontSize="8.5" fill="#7a2e00" fontWeight="700">→ Threshold Galling Stress (TGS)</text>
          </g>;
        })()}

        <line x1={248} y1={24} x2={248} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Test 2: Pin-on-disc ── */}
        {(()=>{
          const ox=268, oy=55;
          return <g>
            <text x={ox+90} y={oy-16} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Pin-on-Disc (ASTM G133)</text>
            {/* Disc body — drawn as two ellipses for 3D effect */}
            <ellipse cx={ox+90} cy={oy+72} rx={70} ry={20} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
            <ellipse cx={ox+90} cy={oy+52} rx={70} ry={20} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            <text x={ox+90} y={oy+55} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">DISC</text>
            {/* Wear track dashed ellipse */}
            <ellipse cx={ox+90} cy={oy+52} rx={42} ry={12} fill="none" stroke="#8a1010" strokeWidth="2" strokeDasharray="5,3"/>
            {/* wear track label — to the left so it doesn't overlap DISC */}
            <text x={ox+24} y={oy+38} fontSize="8.5" fill="#8a1010">wear track</text>
            {/* Pin */}
            <rect x={ox+118} y={oy-8} width={16} height={48} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
            <ellipse cx={ox+126} cy={oy+40} rx={8} ry={5} fill="#7a2e00" stroke="#5a1e00" strokeWidth="1.5"/>
            {/* Load arrow — above pin, F_N label to right */}
            <line x1={ox+126} y1={oy-18} x2={ox+126} y2={oy-4} stroke="#7a2e00" strokeWidth="2.5" markerEnd="url(#arTE)"/>
            <text x={ox+144} y={oy-10} fontSize="9" fill="#7a2e00" fontWeight="700">F_N</text>
            {/* Rotation arrow — below disc, label to the right so it doesn't overlap */}
            <path d={`M ${ox+24} ${oy+68} A 68 18 0 0 1 ${ox+156} ${oy+68}`}
              fill="none" stroke="#c45a10" strokeWidth="2" markerEnd="url(#arTE)"/>
            <text x={ox+134} y={oy+92} fontSize="8.5" fill="#c45a10">rotate</text>
            {/* Description */}
            <rect x={ox} y={oy+98} width={180} height={84} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+90} y={oy+112} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Measures:</text>
            <text x={ox+90} y={oy+126} textAnchor="middle" fontSize="8.5" fill="#a04010">Friction coefficient μ vs time</text>
            <text x={ox+90} y={oy+140} textAnchor="middle" fontSize="8.5" fill="#a04010">Wear rate (mm³/N·m)</text>
            <text x={ox+90} y={oy+154} textAnchor="middle" fontSize="8.5" fill="#a04010">Galling onset (μ jump)</text>
            <text x={ox+90} y={oy+174} textAnchor="middle" fontSize="8.5" fill="#7a2e00" fontWeight="700">→ Continuous wear data</text>
          </g>;
        })()}

        <line x1={498} y1={24} x2={498} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Test 3: Scratch test ── */}
        {(()=>{
          const ox=518, oy=50;
          return <g>
            <text x={ox+90} y={oy-14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Scratch / Sclerometry Test</text>
            {/* Sample surface */}
            <rect x={ox+20} y={oy+30} width={140} height={40} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            {/* Indenter */}
            <polygon points={`${ox+50},${oy+4} ${ox+58},${oy+30} ${ox+42},${oy+30}`}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            <rect x={ox+44} y={oy-12} width={14} height={18} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
            {/* Scratch track */}
            <line x1={ox+50} y1={oy+30} x2={ox+140} y2={oy+30}
              stroke="#8a1010" strokeWidth="3"/>
            {/* Width variation along scratch */}
            {[0,20,40,60,80,100].map((dx,i)=>{
              const w=2+i*0.8;
              return <ellipse key={i} cx={ox+50+dx} cy={oy+30} rx={w} ry={1.5}
                fill="#8a1010" opacity="0.7"/>;
            })}
            {/* Motion */}
            <line x1={ox+60} y1={oy+14} x2={ox+130} y2={oy+14}
              stroke="#c45a10" strokeWidth="2" markerEnd="url(#arTE)"/>
            <text x={ox+95} y={oy+10} textAnchor="middle" fontSize="8.5" fill="#c45a10">ramped load</text>
            <rect x={ox} y={oy+86} width={180} height={80} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+90} y={oy+100} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Measures:</text>
            <text x={ox+90} y={oy+114} textAnchor="middle" fontSize="8.5" fill="#a04010">Critical load L_c (coating failure)</text>
            <text x={ox+90} y={oy+126} textAnchor="middle" fontSize="8.5" fill="#a04010">Coating adhesion strength</text>
            <text x={ox+90} y={oy+138} textAnchor="middle" fontSize="8.5" fill="#a04010">Hardness profile</text>
            <text x={ox+90} y={oy+150} textAnchor="middle" fontSize="8.5" fill="#7a2e00" fontWeight="700">→ Coating qualification</text>
          </g>;
        })()}

        <line x1={748} y1={24} x2={748} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Test 4: Field inspection ── */}
        {(()=>{
          const ox=768, oy=50;
          return <g>
            <text x={ox+90} y={oy-14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Field Inspection / Grading</text>
            {/* Visual severity scale */}
            {[
              { grade:'0', label:'No galling', fill:'#5a7820', y:oy+6 },
              { grade:'1', label:'Slight smearing', fill:'#c4a010', y:oy+50 },
              { grade:'2', label:'Moderate galling', fill:'#c45a10', y:oy+94 },
              { grade:'3', label:'Severe galling', fill:'#8a1010', y:oy+138 },
            ].map((g,i)=>(
              <g key={i}>
                <rect x={ox+10} y={g.y} width={30} height={36} rx={2} fill={g.fill} stroke="#7a2e00" strokeWidth="1.5"/>
                {i>0 && Array.from({length:i*2},(_,j)=>(
                  <line key={j} x1={ox+10} y1={g.y+6+j*5} x2={ox+40} y2={g.y+3+j*5}
                    stroke="#1a0e06" strokeWidth="1.2" opacity="0.5"/>
                ))}
                <text x={ox+14} y={g.y+22} fontSize="11" fontWeight="700" fill="#fdf4ec">{g.grade}</text>
                <text x={ox+46} y={g.y+14} fontSize="9.5" fontWeight="700" fill="#7a2e00">{g.label}</text>
                {i===0 && <text x={ox+46} y={g.y+26} fontSize="8.5" fill="#a04010">Surface intact</text>}
                {i===1 && <text x={ox+46} y={g.y+26} fontSize="8.5" fill="#a04010">Ra increase &lt;2×</text>}
                {i===2 && <text x={ox+46} y={g.y+26} fontSize="8.5" fill="#a04010">Visible transfer lumps</text>}
                {i===3 && <text x={ox+46} y={g.y+26} fontSize="8.5" fill="#a04010">Seizure risk / replace</text>}
              </g>
            ))}
            <rect x={ox} y={oy+188} width={180} height={56} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
            <text x={ox+90} y={oy+202} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">ASTM G196:</text>
            <text x={ox+90} y={oy+216} textAnchor="middle" fontSize="8.5" fill="#a04010">Standardised visual grading</text>
            <text x={ox+90} y={oy+228} textAnchor="middle" fontSize="8.5" fill="#a04010">Used with G98 threshold test</text>
            <text x={ox+90} y={oy+240} textAnchor="middle" fontSize="8.5" fill="#7a2e00" fontWeight="700">Grade 2 = galling threshold</text>
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>ASTM G98 (button-on-block) is the standard test for determining Threshold Galling Stress (TGS) — the minimum contact pressure at which ASTM G196 Grade 2 damage appears after one rotation; higher TGS = better galling resistance</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function GallingPage() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Galling — Mechanical Engineering Reference</h1>

      <p style={S.p}>
        <strong>Galling</strong> is a form of severe adhesive wear that occurs when two sliding surfaces
        in intimate contact experience localised welding (adhesion) at asperity contact points, followed
        by tearing of material from one or both surfaces. Unlike mild abrasive or oxidative wear — which
        progress gradually and predictably — galling can cause catastrophic surface damage and complete
        joint seizure after only a single assembly or disassembly operation. It is particularly prevalent
        in stainless steel, titanium, and aluminium components under high contact pressure with little or
        no lubrication, and is responsible for a large proportion of threaded fastener, valve, and
        precision fit failures in industry.
      </p>

      <h2 style={S.h2}>Mechanism — How Galling Initiates and Propagates</h2>
      <p style={S.p}>
        Galling begins at the microscopic level when surface asperities (micro-peaks) from opposing
        surfaces make contact under normal load. If the local contact stress exceeds the material yield
        strength — which can occur at pressures far below the nominal contact pressure because real contact
        area is only a small fraction of nominal area — plastic deformation and adhesive welding occurs.
        The adhesive junction is typically stronger than the surrounding material, so subsequent sliding
        tears the material rather than shearing the junction, producing craters on one surface and
        transferred lumps on the other.
      </p>
      <DiagramMechanism />
      <Table2 rows={[
        ['Adhesive wear family','Galling is the severe end of the adhesive wear spectrum:\nMild adhesive wear → material transfer at atomic level, surface looks normal\nModerate adhesive wear → micro-scratches, gradual Ra increase\nSevere adhesive wear (galling) → macro craters, lumps, seizure\nScuffing (in gears/bearings) → same mechanism, different terminology'],
        ['Real vs nominal contact area','Nominal contact area A_nom = geometric overlap (m²)\nReal contact area A_real = sum of asperity contact spots ≈ F_N / H (Bowden-Tabor)\nFor metals: A_real ≈ 0.001 – 0.01 × A_nom\nLocal contact pressure P_real = F_N / A_real → often exceeds σ_yield even at low F_N'],
        ['Adhesion mechanism','Adhesion strength τ_adhesion > τ_shear of base metal when:\n  1. Both surfaces have the same crystal structure (FCC stainless, FCC Ti)\n  2. Oxide films are disrupted by plastic deformation\n  3. No lubricant separates the surfaces\nResult: junction shear strength > material shear strength → tearing, not sliding'],
        ['Work hardening role','Austenitic stainless (304, 316) work-hardens rapidly during sliding\nWork-hardened layer INCREASES adhesion potential (less ductility = less energy absorption)\nFerrous / martensite steels work-harden less → lower galling tendency\nHigher initial hardness: more resistance (asperity peaks do not deform and weld as easily)'],
        ['Temperature effect','Frictional heat at interface: ΔT ≈ μ·P·v / (k·A_real)  (flash temperature)\nHigher temperature: lower yield strength → more plastic deformation → more adhesion\nFor self-mated SS at v > 1 m/s: flash temperature can reach 500–800°C at asperity\nMaterial selection for hot applications: Inconel, Hastelloy, Nitronic 60 preferred over 304/316'],
      ]}/>

      <h2 style={S.h2}>Material Pair Compatibility</h2>
      <DiagramMaterialMatrix />
      <Table2 rows={[
        ['Worst combinations','Austenitic stainless (304/316) self-mated: highest galling tendency\nTitanium alloys self-mated: very severe galling\nAluminium self-mated: severe galling\nAny FCC metal against itself: high adhesion tendency\nAny identical material pair: avoid whenever contact pressure and sliding are present'],
        ['Why SS is worst','Face-centred-cubic (FCC) crystal structure: close-packed planes slide easily\nHigh ductility: large plastic deformation at asperities → large contact area\nPassive oxide film: disrupted instantly by sliding → bare metal-to-metal contact\nWork-hardening: rapid hardening creates stress concentrations\nSimilar solubility: SS pairs are metallurgically compatible → strong adhesive bonds'],
        ['Good combinations','Bronze/brass against steel or SS: hexagonal Cu lattice vs FCC Fe → poor adhesion\nBronze bushings in SS bores: industry standard for galling-resistant sliding bearings\nCast iron against steel: graphite acts as built-in solid lubricant\nNitrided steel against SS: hard nitrided layer (700–1100 HV) vs 160 HV SS → no adhesion\nNitronic 60 against itself: nitrogen-alloyed SS; significantly better than 304 self-mated'],
        ['Hardness mismatch rule','Key rule: hardness difference ≥ 100 HV significantly reduces galling\nIdeal ratio: harder material ≥ 1.5× softer material Vickers hardness\nHarder surface: asperities do not deform and weld as readily\nSofter surface: does deform but at lower stress = less tendency to tear vs shear cleanly\nNote: BOTH very soft (pure Al) AND very hard (identical ceramics) can still gall'],
        ['Nitronic alloys','Nitronic 60 (21-6-9): specially developed galling-resistant austenitic SS\nContains silicon + manganese additions that inhibit adhesion\nTGS (Threshold Galling Stress) self-mated: ~69 MPa vs ~7 MPa for 304 self-mated\nUsed: valve stems, pump shafts, food processing, marine hardware\nCost: 3–5× standard 304 — justified only where galling is known risk'],
      ]}/>

      <h2 style={S.h2}>Galling Threshold — Contact Pressure &amp; Speed</h2>
      <DiagramThresholdMap />
      <Table2 rows={[
        ['Threshold Galling Stress','TGS (ASTM G98): minimum contact pressure that causes ASTM G196 Grade 2 galling\nin one rotation of a button on a flat block, unlubricated, at room temperature\nSS 304 self-mated: TGS ≈ 7–14 MPa (very low — easily exceeded in practice)\nNitronic 60 self-mated: TGS ≈ 69 MPa\nNitrided 4140 vs SS: TGS > 200 MPa'],
        ['PV limit','PV = contact pressure × sliding velocity  (MPa·m/s or psi·fpm)\nFor many material pairs: P×V = constant at the galling boundary\nExceeding PV limit: film breakdown → rapid galling onset\nSS 304 dry: PV ≈ 0.05–0.10 MPa·m/s\nBronze/steel lubricated: PV ≈ 1–5 MPa·m/s\nBearing bronzes: rated to PV ≈ 0.5–3.5 MPa·m/s (catalogue values)'],
        ['Speed influence','Low speed (bolt assembly, 0.01–1 mm/s): threshold is HIGHER (more time for oxide recovery)\nMedium speed (valve operation, 10–100 mm/s): moderate threshold\nHigh speed (pump plunger, > 500 mm/s): threshold drops sharply\nFrictional heat at high speed: reduces yield strength → promotes adhesion\nOptimum speed range for a given material/lubricant combination exists'],
        ['Contact geometry effect','Conformal contact (cylinder in bore): lower peak pressure for given force → lower galling risk\nNon-conformal (ball/sphere, knife-edge): extreme contact stress concentration → high risk\nThread flanks: contact on 60° inclined surface at root → stress concentration\nRunning-in: initial operation can either reduce risk (smoothing asperities) or trigger immediate galling'],
        ['Temperature effect on threshold','At 300°C: TGS drops 30–50% from room temperature value\nAt 500°C: TGS drops 60–70% — galling easily triggered\nFor hot applications: refractories, Stellite, Inconel, or ceramics required\nCryogenic: TGS generally increases (higher yield strength) BUT oxide films may be thinner'],
      ]}/>

      <h2 style={S.h2}>Surface Finish &amp; Hardness Effects</h2>
      <DiagramSurfaceHardness />
      <Table2 rows={[
        ['Optimum surface finish','For unlubricated contacts: Ra 0.4–0.8 μm (smoothest practical = best)\nFor lubricated contacts: Ra 0.8–1.6 μm (valleys retain lubricant film)\nToo smooth (Ra < 0.1 μm): lubricant cannot be retained in valleys → boundary layer breaks down\nToo rough (Ra > 3.2 μm): asperity peaks too tall → individual asperities carry entire load → galling'],
        ['Surface lay direction','Unidirectional lay perpendicular to sliding: worst (asperities all aligned to contact simultaneously)\nMulti-directional (cross-hatch): better — asperities stagger their contact\nIsotropic finish (lapping, electropolishing): best for sliding contacts\nFor bolted joints: electropolished SS fasteners show 2–4× higher TGS than turned surface'],
        ['Hardness measurement','Vickers (HV): most common for engineering surfaces; 1 kgf standard load\nRockwell (HRC): useful for hardened tool steels; HRC 60 ≈ HV 746\nShore D: for polymers / very soft materials in PTFE-lined joints\nMicro-Vickers: for measuring case-hardened layer, nitriding depth (50 g load typical)'],
        ['Nitriding depth effect','Compound layer (white layer): 5–25 μm, very hard (900–1200 HV)\nDiffusion zone: 0.1–0.5 mm, hardness gradient\nFor galling resistance: compound layer alone is often sufficient\nThrough-hardened substrate needed if point loading causes Hertzian stress penetrating compound layer\nNitriding does not change part dimensions significantly (< 0.01 mm growth): net-shape process'],
        ['Shot peening interaction','Shot peening compresses surface → higher compressive residual stress\nCompressive stress closes micro-cracks → better fatigue life\nEffect on galling: minor — increases hardness slightly but rough surface from peening can increase risk\nFor galling resistance: peen then finish grind / polish to Ra 0.8 μm for best combined result'],
      ]}/>

      <h2 style={S.h2}>Lubrication &amp; Surface Treatment Strategies</h2>
      <DiagramLubrication />
      <Table2 rows={[
        ['Anti-seize compounds','Copper-based: most common; works to 980°C; contains Cu flakes in grease carrier\nNickel-based: for high-temperature SS (304/316/Inconel); PTFE/silicone free\nMolybdenum disulphide (MoS₂) paste: excellent for threads; works to 400°C in air\nApplication: thin coat on male thread, nut face, under bolt head\nTorque correction: reduce target torque by 15–25% when anti-seize applied (K factor changes)'],
        ['MoS₂ (molybdenum disulphide)','Lamellar hexagonal structure: Mo-S planes slide over each other with μ ≈ 0.02–0.08\nTemperature range: −180°C to +400°C in air; up to 1100°C in vacuum/inert gas\nApplication: spray, burnish, or paste into thread/bore\nLimitation: sensitive to humid air (oxidises above 400°C); avoid with oxidising environments\nFilm thickness: 5–25 μm; periodically reapplied or integrated into PTFE-lined bushing'],
        ['PTFE / Teflon','Very low μ ≈ 0.04–0.10; wide temperature range −200°C to +260°C\nUsed as: filled polymer bushings, thread tape (PTFE tape for pipe threads), powder blending\nLimitation: high creep under sustained load; low wear resistance vs metal\nPTFE-filled PEEK: better creep + temperature + galling resistance\nComposite bushings (bronze + PTFE): combines metal strength with PTFE low friction'],
        ['Physical Vapour Deposition (PVD) coatings','TiN (titanium nitride): gold colour, HV 2300, μ ≈ 0.4–0.6 dry; 2–5 μm thick\nCrN (chromium nitride): silver-grey, HV 1800, excellent corrosion resistance\nDLC (diamond-like carbon): HV 1500–3000, μ ≈ 0.05–0.15 dry — best dry friction\nAlTiN: highest hardness (HV 3000+), best for cutting tools to 900°C\nAll PVD: applied at 200–500°C; does not distort hardened parts; ≤ 0.01 mm dimensional change'],
        ['Chemical treatments','Ferritic nitrocarburising (FNC / Tenifer): SS dipped in salt bath → thin ε-iron nitride layer\nNitriding: gas, plasma, or salt bath; 450–580°C; 0.05–0.5 mm case\nCarburising: adds C to surface → martensite case; 850–950°C; distortion risk\nBlack oxide: Fe₃O₄ layer; small improvement in galling resistance; minimal dimensional change\nHard chrome plating: HV 900–1100; now largely replaced by HVOF and PVD due to Cr(VI) regulation'],
      ]}/>

      <h2 style={S.h2}>Common Galling Failure Locations</h2>
      <DiagramFailureScenarios />
      <Table2 rows={[
        ['Bolted joints — most common','Torque-induced combined normal + sliding load on thread flanks\nSS bolts in SS tapped holes: near-100% chance of galling during assembly if un-lubricated\nSolution priority: 1) Anti-seize compound  2) Nitronic 60 fasteners  3) Bronze nut insert\nRe-assembly after galling: never tighten a galled bolt; threads must be replaced\nTorque specification: always state lubricant condition in bolt torque tables (dry vs lubricated)'],
        ['Valve stems and gates','High-cycle application: 10,000–1,000,000 open/close cycles\nSS valve stem in SS guide: classic galling failure in chemical plant\nSolution: Stellite 6 hard-facing on stem (HV 400–500); Nitronic 60 stem in SS guide\nAlternative: PTFE-lined guide; self-lubricating sintered bronze guide\nDetection: increasing operating torque; visual inspection on planned maintenance'],
        ['Threaded rods and unions','Pipe unions and tube fittings: SS to SS → seize during make-up\nSolution: silver-plated or zinc-plated male threads; PTFE tape for low-pressure; Jet-Lube or equivalent\nOilfield tubing (API threads): premium threads with specific API-approved thread compound\nFor high-temperature SS piping: Molykote P-37 or equivalent Ni-based compound'],
        ['Splines and sliding fits','Axially sliding splines under torque: highest risk when interference fit AND axial travel\nSolution: case-harden teeth (HRC 58–62) + MoS₂ coating + light grease\nPrecision sliding fits (optical, machine tools): bronze bushing or PTFE composite\nKeyways: key sides can gall if fits are loose and fretting occurs under cyclic torque'],
        ['Cam followers and valve train','High Hertz contact stress (≥1 GPa): extreme galling risk without EHL film\nRequires: dedicated cam lube (zinc dithiophosphate additive) or DLC-coated cam\nRunning-in: most cam galling occurs in first 500 miles — critical break-in oil recommended\nHard coating: DLC on bucket lifters reduces μ from 0.12 to 0.03 in boundary regime'],
      ]}/>

      <h2 style={S.h2}>Prevention Decision Flow</h2>
      <DiagramPreventionFlow />
      <Table2 rows={[
        ['Priority 1: Material selection','Always first choice: if design allows, specify dissimilar material pairs\nSS fastener in non-SS structure: usually acceptable\nWhere both must be SS: specify Nitronic 60, 17-4PH (hardened), or 2205 duplex\nDuplex SS (2205): higher hardness than 316, better galling resistance in self-mated condition'],
        ['Priority 2: Surface hardening','Nitriding: most cost-effective for complex shapes (no distortion)\nCase hardening (carburising): excellent case depth, higher distortion risk\nFlame/induction hardening: local hardening of flanges, spline teeth\nElectroless nickel (EN): HV 500–700 as-plated; up to HV 1000 after 400°C bake\nHard chrome: still specified for large bores; Cr(VI) replacement pressure → HVOF thermal spray'],
        ['Priority 3: Lubrication','Thread lubrication: always apply anti-seize for SS-to-SS or SS-to-Ti joints\nKey rule: never assemble dry SS threads in SS tapped holes under any circumstances\nPersistent lubrication: sealed bearing design to maintain lubricant over service life\nSolid lubricant injection: burnished MoS₂ into bore surface for periodic-maintenance-free joints'],
        ['Priority 4: Coating','When geometry makes material/hardness change impractical: apply PVD hard coating\nTiN standard; CrN for corrosive environments; DLC for lowest friction\nCoating selection: check compatibility with operating temperature (DLC limited to 400°C)\nCoating adhesion: ground surface to Ra 0.4 μm before PVD; coating cannot bridge defects'],
        ['Priority 5: Design geometry','Reduce contact pressure: increase bearing area (longer thread engagement, larger bore)\nAvoid conformal stress concentration: specify controlled interference fits\nAllow for running-in: design includes initial lubrication; monitor first assembly torque\nMaterial stack-up: ensure hardest component contacts softest opposing surface'],
        ['Inspection and maintenance','Define inspection interval based on TGS/PV analysis and service history\nVisual grading per ASTM G196: Grade 0 acceptable, Grade 1 monitor, Grade 2 replace\nTorque trending: if required torque increases beyond ±25% from baseline → inspect\nNever reuse galled fasteners: the transferred material creates abrasive third-body particles'],
      ]}/>

      <h2 style={S.h2}>Testing &amp; Characterisation</h2>
      <DiagramTesting />
      <Table2 rows={[
        ['ASTM G98 (button-on-block)','Standard test for Threshold Galling Stress (TGS)\nButton: 25.4 mm diameter cylindrical flat end\nBlock: 50 mm × 50 mm flat coupon\nProcedure: apply load, rotate button 1 revolution, inspect at 10× magnification\nResult: highest load with no galling = TGS (MPa)  ·  Repeat at each stepped load level\nTypical TGS values: SS 304 dry = 7–14 MPa; Nitronic 60 dry = 69 MPa; Nitrided = >200 MPa'],
        ['ASTM G196 visual grading','Grade 0: no damage — same appearance as unworn reference\nGrade 1: scratches/smearing — no raised material; polishing/cleaning removes\nGrade 2: galling threshold — raised material visible; cannot be removed by polishing\nGrade 3: severe galling — large lumps, craters; surface requires machining to restore\nGrade 4: seizure — surfaces fused or fractured; destructive disassembly required'],
        ['ASTM G133 (pin-on-disc)','Gives continuous wear and friction data vs sliding distance\nCan determine transition from mild to severe (galling) wear\nCoefficient of friction: monitored in real time; sudden jump = galling onset\nWear volume: measured by profilometry of wear track; lower = better\nTest conditions standardised for each material class'],
        ['Hertz contact analysis','For design verification: compute max contact pressure P_max\nSpherical contact: P_max = (6FE*²/π³R²)^(1/3)  where E* = combined modulus, R = radius\nCylinder-on-flat: P_max = (F·E*)/(π·L·R·π/4)^(0.5) per unit length\nCheck: P_max < TGS/SF  with SF ≥ 2–3 for critical joints\nNote: TGS is measured at a specific geometry — apply contact mechanics correction'],
        ['Profilometry and SEM','Surface profilometry: Ra, Rz, Rmax before and after contact → quantifies damage\nSEM (scanning electron microscope): imaging of transfer layers, adhesion junctions\nEDX analysis: identifies which material has transferred to which surface\nCross-section metallography: depth of plastic deformation, work-hardened layer'],
      ]}/>

      <h2 style={S.h2}>Relevant Standards &amp; Specifications</h2>
      <Table2 rows={[
        ['ASTM G98','Standard test for Threshold Galling Stress — button-on-block method\nDefines specimen geometry, surface preparation, test load steps, and reporting\nResult: TGS in MPa; basis for material comparison and design verification'],
        ['ASTM G196','Standard practice for galling visual assessment scale (Grades 0–4)\nUsed in conjunction with G98 for determining galling threshold\nGrade 2 = galling threshold by convention'],
        ['ASTM G133','Standard test for sliding wear using a linearly reciprocating ball-on-flat configuration\nSeparate standard for pin-on-disc: ASTM G99\nUse for lubricant evaluation and coating screening'],
        ['ISO 19252','Standard for scuffing load-carrying capacity of lubricants (FZG test)\nRelevant for gear lubricants — similar mechanism to galling in gear tooth flanks\nResult: FZG pass load stage (1–12); higher = better scuffing resistance'],
        ['ASME B1.1 / B1.13M','Thread standards — specify thread class (tolerance) for different engagement applications\nTighter class (2A/2B) → less radial play → more uniform contact distribution → lower peak pressure\nAnti-seize torque correction factors referenced in many industry standards'],
        ['API 5CT / API 5B','Oilfield casing and tubing thread standards\nSpecify required thread compound for make-up torque compliance\nDeft compound application technique specified — over-application can hydraulically lock coupling'],
        ['NACE MR0175 / ISO 15156','Material requirements for sulphide stress cracking environments (sour service)\nSpecifies hardness limits (e.g. ≤ 22 HRC for carbon steel) which indirectly affect galling resistance\nHarder materials preferred for galling but softer required for H₂S resistance — conflict to manage'],
      ]}/>

      <InfoBox title="Galling Key Facts &amp; Design Rules" items={[
        "Definition: severe adhesive wear — macro material transfer (craters + lumps) + potential seizure in few cycles",
        "Worst material pair: austenitic SS 304/316 self-mated, unlubricated — TGS ≈ 7 MPa",
        "Best single fix: dissimilar material pair (e.g. bronze bushing in SS bore)",
        "Hardness rule: aim for ≥ 100 HV difference between mating surfaces",
        "Critical SS combinations to ALWAYS lubricate: SS bolt in SS tapped hole; SS valve stem in SS guide",
        "Anti-seize torque correction: reduce target torque 15–25% when anti-seize applied (K factor ~0.13 vs ~0.20 dry)",
        "TGS (Threshold Galling Stress): SS 304 dry ≈ 7–14 MPa  ·  Nitronic 60 ≈ 69 MPa  ·  Nitrided steel > 200 MPa",
        "Optimal surface finish for lubricated sliding: Ra 0.8–1.6 μm (valleys retain lubricant)",
        "PV limit guide: SS dry ≈ 0.05 MPa·m/s  ·  Bronze/steel lubricated ≈ 2–5 MPa·m/s",
        "MoS₂ friction coefficient: μ ≈ 0.02–0.08 (vs μ ≈ 0.6–1.2 for dry SS/SS)",
        "DLC coating: μ ≈ 0.05–0.15 dry — best solid surface treatment for galling resistance",
        "ASTM G196 Grade 2 = galling threshold  ·  Grade 4 = seizure  ·  always replace Grade 3+",
      ]}/>
    </div>
  );
}